import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useSiteToggles } from '../../hooks/useSiteToggles'
import { isSiteMode } from '../../lib/siteMode'
import ParrotMark from '../common/ParrotMark'

/**
 * The sign-in page's own frame (auth-frame, owner's decisions 2026-10-14):
 * no site header, nav or footer, in the website and the installed app
 * alike — one column like the Android login, a single centred card from
 * `sm` up. Only a brand row with the small language and theme switches,
 * the step itself, and the «کد نمی‌رسد؟» row under it.
 *
 * Height: `min-h-svh`, never `h-dvh overflow-hidden`. Laid out to fit the
 * smallest real viewport (≈320×460, an iPhone SE in Safari) with no
 * scroll; when the keyboard or a landscape phone makes the page shorter,
 * the browser's own scroll stays as the safety net, so nothing is ever
 * trapped behind the keyboard. Short heights (`max-height: 520px`, or the
 * keyboard detected through visualViewport → `kb-open`) drop the lead line
 * and tighten the spacing.
 *
 * While mounted it also asks the browser to resize the layout for the
 * keyboard (`interactive-widget=resizes-content`, Chrome 108+), and puts
 * the old viewport meta back on the way out — the rest of the app keeps
 * the default.
 */
export default function AuthFrame({ title, footer, children }) {
  const { t } = useTranslation('auth')
  const { language, dark, languageLocked, toggleLanguage, toggleTheme } = useSiteToggles()
  const site = isSiteMode()
  const keyboardOpen = useKeyboardOpen()

  useEffect(() => {
    if (title && typeof document !== 'undefined') document.title = title
  }, [title])

  useResizesContent()

  const brand = (
    <>
      <ParrotMark size={30} />
      <span className="font-display text-xl font-extrabold tracking-[-0.2px]">{t('brand')}</span>
    </>
  )

  return (
    <div
      data-auth-frame
      className={`group/auth flex min-h-svh flex-col bg-ground text-ink ps-[max(16px,env(safe-area-inset-left))] pe-[max(16px,env(safe-area-inset-right))] pt-[max(6px,env(safe-area-inset-top))] pb-[max(6px,env(safe-area-inset-bottom))] ${
        keyboardOpen ? 'kb-open' : ''
      } ${language === 'en' ? 'font-en' : ''}`}
    >
      <header className="mx-auto flex h-12 w-full max-w-[440px] shrink-0 items-center justify-between sm:max-w-[1140px]">
        {site ? (
          <Link to="/" className="flex items-center gap-2" aria-label={t('home')}>
            {brand}
          </Link>
        ) : (
          <span className="flex items-center gap-2">{brand}</span>
        )}
        <div className="flex items-center gap-1.5">
          {!languageLocked && (
            <button
              type="button"
              onClick={toggleLanguage}
              lang={language === 'fa' ? 'en' : 'fa'}
              aria-label={t('otherLanguageLabel')}
              className={`${ROUND} px-3 ${language === 'fa' ? 'font-en text-[13px] tracking-[0.3px]' : 'text-[13.5px]'}`}
            >
              {t('otherLanguage')}
            </button>
          )}
          <button type="button" onClick={toggleTheme} aria-label={dark ? t('toLight') : t('toDark')} className={`${ROUND} w-10`}>
            {dark ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </header>

      <main className="flex flex-1 flex-col pt-[clamp(6px,4svh,40px)] group-[.kb-open]/auth:pt-1 sm:items-center sm:justify-center sm:pt-6 [@media(max-height:520px)]:pt-1">
        <section className="mx-auto w-full max-w-[440px] sm:rounded-[28px] sm:border-2 sm:border-line sm:bg-surface sm:p-8 sm:shadow-[0_8px_0_var(--color-line)] [@media(max-height:520px)]:sm:p-5">
          {children}
        </section>
      </main>

      {footer && <footer className="mx-auto flex w-full max-w-[440px] shrink-0 items-center justify-center pt-1">{footer}</footer>}
    </div>
  )
}

/** 40px circles with a 44px hit area (the 2px gap padding is the difference). */
const ROUND =
  'inline-flex h-10 min-w-10 items-center justify-center rounded-full border-2 border-line bg-surface font-display font-bold text-ink-2 transition hover:text-ink outline-offset-2'

/**
 * Whether the on-screen keyboard is up, from visualViewport (iOS and any
 * Chrome that ignored interactive-widget): the visual viewport shrinks
 * well below the layout one. The page then drops what can go.
 */
function useKeyboardOpen() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport
    if (!viewport) return undefined
    const check = () => setOpen(viewport.height < window.innerHeight * 0.75)
    viewport.addEventListener('resize', check)
    check()
    return () => viewport.removeEventListener('resize', check)
  }, [])
  return open
}

function useResizesContent() {
  useEffect(() => {
    const meta = typeof document === 'undefined' ? null : document.querySelector('meta[name="viewport"]')
    if (!meta) return undefined
    const before = meta.getAttribute('content') ?? ''
    if (!/interactive-widget/.test(before)) meta.setAttribute('content', `${before}, interactive-widget=resizes-content`)
    return () => meta.setAttribute('content', before)
  }, [])
}

function SunIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4m11.4-11.4 1.4-1.4" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  )
}
